
export enum GameStatus {
  DASHBOARD = 'DASHBOARD',
  GENERATING_CASE = 'GENERATING_CASE',
  TRAVELING = 'TRAVELING',
  INVESTIGATING = 'INVESTIGATING',
  INTERROGATING = 'INTERROGATING',
  CASE_SOLVED = 'CASE_SOLVED',
  GAME_OVER = 'GAME_OVER'
}

export interface LocationClue {
  description: string;
  unlocksLocations?: string[]; // IDs of locations to reveal
  unlocksCharacters?: string[]; // IDs of characters to reveal
}

export interface Location {
  id: string;
  name: string;
  description: string;
  evidence?: string; // Legacy string description
  clues?: LocationClue[]; // New structured clues array
  openTime: string; // Format "HH:mm"
  closeTime: string; // Format "HH:mm"
  x: number; // Coordinate X (0-100)
  y: number; // Coordinate Y (0-100)
  type: 'public' | 'private' | 'restricted';
  isCrimeScene?: boolean; // New: If true, always accessible
}

export interface Clue {
  id: string;
  name: string;
  description: string;
  foundAt: string;
  isInspectable?: boolean;
  inspectResult?: {
    story: string;
    unlocksLocations?: string[];
    unlocksCharacters?: string[];
  };
}

export interface Character {
  id: string;
  name: string;
  role: 'witness' | 'suspect' | 'expert';
  locationId: string;
  description: string;
  phoneStatus: 'available' | 'busy' | 'offline'; // New: Phone mechanic
  arrivalAtHQ?: number; // Timestamp when they arrive at HQ if summoned
}

export interface CaseData {
  id: string; // Unique ID for the case
  title: string;
  description: string;
  locations: Location[];
  characters: Character[];
  solution: {
    culpritId: string;
    motive: string;
  };
  createdAt: number; // Timestamp
  deadline: number; // Timestamp (e.g., 3 days later)
  status: 'active' | 'solved' | 'failed'; // Player instance status

  // --- Admin / Publishing Fields (Phase 2) ---
  publishStatus?: 'draft' | 'published';
  scheduledDate?: number; // Timestamp of when it becomes available
  isExtra?: boolean;
  category?: string; // e.g., 'Dezembro 2024' or 'Tutorial'
  difficulty?: 'Fácil' | 'Médio' | 'Difícil';
  estimatedTime?: 'Curto' | 'Médio' | 'Longo';
  isSpecialSeason?: boolean;
  heroImageUrl?: string; // Phase 3: Firebase Storage
  thumbImageUrl?: string; // Phase 3: Firebase Storage

  // --- Per-case persistence state ---
  lastSavedTime: number; // Stores the specific gameTime for this case
  currentLocationId: string | null;

  // Fog of War & Discovery
  discoveredLocationIds: string[]; // Locations known to exist
  scoutedLocationIds: string[]; // Locations thoroughly searched (reveals suspects)
  visitedLocationIds: string[]; // Locations physically entered

  // Social & Intel
  metCharacterIds: string[]; // IDs of characters interacted with
  knownCharacterNames: Record<string, boolean>; // Map ID -> true if name is revealed

  inventory: Clue[];
  logs: LogEntry[];
  userNotes: string;
  suspectAnnotations: Record<string, string>;
}

export interface LogEntry {
  timestamp: string;
  type: 'system' | 'dialogue' | 'clue';
  content: string;
  details?: string;
}

export interface GameState {
  status: GameStatus;
  dashboardView: 'main' | 'daily_list' | 'acme_club'; // New: Sub-navigation state
  activeCases: CaseData[]; // List of active cases (max 3)
  archivedCases: CaseData[]; // Solved or failed cases
  selectedCaseId: string | null; // Currently playing case

  // Global Travel State
  travelStatus: 'idle' | 'moving';
  travelDestinationId: string | null;
  travelArrivalTime: number | null;
  travelTotalDuration: number | null;

  nextActionTime: number | null; // Cooldown
  gameTime: number; // Current ACTIVE simulated timestamp
  viewedTutorials?: string[]; // e.g. ['welcome', 'map', 'investigation', 'interrogation', 'inventory']
}

export type StoreItemType = 'lupa' | 'vip' | 'case' | 'keys';

export interface StoreItem {
  id: string;
  title: string;
  description: string;
  type: StoreItemType;
  price: number;
  discount?: number; // percentage, e.g., 20 for 20% off
  rewardValue: number | string; // amount of lupas OR days of VIP OR caseId
  isActive: boolean;
  createdAt?: any; // Firestore Timestamp
  iconUrl?: string;
  highlight?: string; // e.g. "MAIS POPULAR"
}

export interface SaleHistory {
  id: string;
  buyerId: string;
  buyerName: string;
  buyerEmail: string;
  itemId: string;
  itemTitle: string;
  itemType: StoreItemType;
  pricePaid: number;
  paymentStatus: 'pending' | 'approved' | 'failed';
  purchasedAt: any; // Firestore Timestamp
  mpPreferenceId?: string;
  mpPaymentId?: string;
}

export interface Announcement {
  id: string;
  title: string;
  text: string;
  imageUrl?: string;
  isActive: boolean;
  createdAt: any; // Firestore Timestamp
  expiresAt: any; // Firestore Timestamp
}

export interface RankReward {
  level: number;
  rewardType: 'lupas' | 'keys' | 'vip_days';
  rewardAmount: number;
}
